import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useInView } from 'react-intersection-observer';
import { BlueskyService } from '../services/bluesky';
import { BookmarkService } from '../services/BookmarkService';
import { Storage } from '../utils/storage';
import { useI18n } from '../i18n';
import Post from './Post';
import LoginForm from './LoginForm';
import CreatePost from './CreatePost';
import Modal from './Modal';
import UserHeader from './UserHeader';
import { FeedTabs } from './FeedTabs';
import SavedPosts from './SavedPosts';
import { COUNTRIES } from '../config/feeds';
import CountryPicker from './CountryPicker';
import { Plus, AlertCircle, Loader2, ArrowUp } from 'lucide-react';

const GovSky = () => {
  const { t } = useI18n();
  const [bluesky] = useState(() => new BlueskyService());
  const [session, setSession] = useState(Storage.getSession());
  const [posts, setPosts] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [activeTab, setActiveTab] = useState('recent');
  const [isChangingFeed, setIsChangingFeed] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [bookmarkService] = useState(() => new BookmarkService());
  const [country, setCountry] = useState(() => {
    return localStorage.getItem('govsky_country') || null;
  });
  const [showScrollTop, setShowScrollTop] = useState(false);
  const containerRef = useRef(null);

  const isFirstVisit = !country;

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
  });

  // Handle scroll to show/hide scroll-to-top button
  const handleScroll = useCallback((e) => {
    const scrollTop = e.target.scrollTop || window.scrollY || document.documentElement.scrollTop;
    setShowScrollTop(scrollTop > 300);
  }, []);

  // Listen for window scroll as well (for iOS)
  useEffect(() => {
    const handleWindowScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      setShowScrollTop(scrollTop > 300);
    };

    window.addEventListener('scroll', handleWindowScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    // Try multiple scroll methods for cross-platform compatibility
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    // Also scroll the window (for iOS)
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Fallback for older browsers
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
	if (isFirstVisit) {
	  setPosts([]);
	}
  }, [isFirstVisit]);

  useEffect(() => {
    let isMounted = true;

    const initializeApp = async () => {
      const savedSession = Storage.getSession();
      if (savedSession) {
        try {
          const response = await bluesky.resumeSession(savedSession);
          if (isMounted) {
            if (response.success) {
              setSession(savedSession);
            } else {
              Storage.clearSession();
              setSession(null);
            }
          }
        } catch (error) {
          if (isMounted) {
            Storage.clearSession();
            setSession(null);
          }
        }
      }
      if (country && isMounted) {
        await refreshFeed(COUNTRIES[country].feeds.recent.uri);
      }
    };

    initializeApp();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
	if (inView && hasMore && !isLoadingMore && !isRefreshing) {
	  loadMorePosts();
	}
  }, [inView, hasMore, isLoadingMore, isRefreshing]);

  const refreshFeed = async (feedUri) => {
	setIsRefreshing(true);
	setError('');
	try {
	  const response = await bluesky.getFeed(feedUri);
	  if (response.success) {
		setPosts(response.data);
		setCursor(response.cursor);
		setHasMore(!!response.cursor);
	  } else {
		setError('Failed to fetch posts: ' + response.error);
	  }
    } catch (err) {
      setError('Network error while fetching posts');
    } finally {
	  setIsRefreshing(false);
	  setIsChangingFeed(false);
	}
  };

  const handleCountryChange = (newCountry) => {
	setCountry(newCountry);
	localStorage.setItem('govsky_country', newCountry);
	
	// Reset feed state
	setActiveTab('recent');
	setPosts([]);
	setCursor(null);
	setHasMore(true);
	
	// Refresh feed with new country's feed URI
	refreshFeed(COUNTRIES[newCountry].feeds.recent.uri);
  };

  const handleTabChange = async (feed) => {
	setActiveTab(feed.id);
	if (feed.id === 'saved') {
	  setIsChangingFeed(false);
	  return;
	}
	setIsChangingFeed(true);
	setPosts([]);
	await refreshFeed(feed.uri);
  };

const handleLogin = async (loginData) => {
	setIsLoading(true);
	setError('');
  
	try {
	  const response = await bluesky.login(
		loginData.identifier, 
		loginData.password,
		loginData.rememberMe
	  );
	  
	  if (response.success) {
		// Pass rememberMe to Storage.setSession
		Storage.setSession(response.data, loginData.rememberMe);
		setSession(response.data);
		setShowLoginModal(false);
		if (country) {
		  const currentFeed = COUNTRIES[country].feeds[activeTab];
		  refreshFeed(currentFeed.uri);
		}
	  } else {
		setError('Login failed: ' + response.error);
	  }
	} catch (err) {
	  setError('Login error: ' + err.message);
	} finally {
	  setIsLoading(false);
	}
  };

  const loadMorePosts = async () => {
	if (!cursor || isLoadingMore || !hasMore || !country) return;
	
	setIsLoadingMore(true);
	try {
	  const feedUri = COUNTRIES[country].feeds[activeTab].uri;
	  const response = await bluesky.getFeed(feedUri, cursor);
	  if (response.success) {
		setPosts(prev => [...prev, ...response.data]);
		setCursor(response.cursor);
		setHasMore(!!response.cursor);
	  } else {
		setError('Error loading more posts');
	  }
    } catch (err) {
      setError('Error loading more posts');
    } finally {
	  setIsLoadingMore(false);
	}
  };

  const handleLogout = () => {
	Storage.clearSession();
	setSession(null);
	setShowCreatePost(false);
	const currentFeed = COUNTRIES[country].feeds[activeTab];
	refreshFeed(currentFeed.uri);
  };

  const handleCreatePost = async (postText) => {
	if (!session) {
	  setShowLoginModal(true);
	  return;
	}

	if (!postText.trim()) {
	  setError('Post cannot be empty');
	  return;
	}

	setIsLoading(true);
	try {
	  const response = await bluesky.createPost(postText);
	  if (response.success) {
		setShowCreatePost(false);
		const currentFeed = COUNTRIES[country].feeds[activeTab];
		refreshFeed(currentFeed.uri);
	  } else {
		setError('Failed to create post: ' + response.error);
	  }
	} catch (err) {
	  setError('Error creating post: ' + err.message);
	} finally {
	  setIsLoading(false);
	}
  };

  // Handle like/unlike - likeUri is the viewer.like value (the like record URI)
  const handleLike = async (uri, cid, likeUri) => {
    if (!session) {
      setShowLoginModal(true);
      return;
    }

    try {
      const isLiked = !!likeUri;
      const response = isLiked 
        ? await bluesky.unlikePost(likeUri) // Pass the like record URI
        : await bluesky.likePost(uri, cid);
      
      if (response.success) {
        // Update posts locally for instant feedback instead of full refresh
        setPosts(prevPosts => prevPosts.map(post => {
          if (post.post?.uri === uri) {
            return {
              ...post,
              post: {
                ...post.post,
                likeCount: isLiked 
                  ? (post.post.likeCount || 1) - 1 
                  : (post.post.likeCount || 0) + 1,
                viewer: {
                  ...post.post.viewer,
                  like: isLiked ? undefined : response.data?.uri
                }
              }
            };
          }
          return post;
        }));
      } else {
        setError('Failed to ' + (isLiked ? 'unlike' : 'like') + ' post');
      }
    } catch (err) {
      setError('Error processing like');
    }
  };

  // Handle repost
  const handleRepost = async (uri, cid, repostUri) => {
    if (!session) {
      setShowLoginModal(true);
      return;
    }

    try {
      const isReposted = !!repostUri;
      const response = isReposted
        ? await bluesky.deleteRepost(repostUri)
        : await bluesky.repost(uri, cid);
      
      if (response.success) {
        // Update posts locally for instant feedback
        setPosts(prevPosts => prevPosts.map(post => {
          if (post.post?.uri === uri) {
            return {
              ...post,
              post: {
                ...post.post,
                repostCount: isReposted 
                  ? (post.post.repostCount || 1) - 1 
                  : (post.post.repostCount || 0) + 1,
                viewer: {
                  ...post.post.viewer,
                  repost: isReposted ? undefined : response.data?.uri
                }
              }
            };
          }
          return post;
        }));
      } else {
        setError('Failed to ' + (isReposted ? 'remove repost' : 'repost'));
      }
    } catch (err) {
      setError('Error processing repost');
    }
  };

  return (
    <div 
      className="app-container" 
      ref={containerRef}
      onScroll={handleScroll}
    >
      {isFirstVisit ? (
        <CountryPicker
          currentCountry={country}
		  onCountryChange={handleCountryChange}
		  isFirstTime={true}
		/>
	  ) : (
		<>
		  <header className="app-header">
			<div className="header-content">
			  <div className="header-left">
				<img 
				  src="/govskyinverted.png" 
				  alt="Govsky" 
				  className="header-logo"
				/>
			  </div>
			  <div className="header-center">
				<CountryPicker
				  currentCountry={country}
				  onCountryChange={handleCountryChange}
				/>
			  </div>
			  <div className="header-right">
				{session ? (
				  <UserHeader 
					user={session}
					onLogout={handleLogout}
					bluesky={bluesky}
					bookmarkService={bookmarkService}
				  />
				) : (
				  <button 
					className="button button-primary"
					onClick={() => setShowLoginModal(true)}
				  >
					{t('header.login')}
				  </button>
				)}
			  </div>
			</div>
		  </header>
		  
          {session && (
            <button
              className="fab"
              onClick={() => setShowCreatePost(true)}
              aria-label="Create new post"
            >
              <Plus size={24} />
            </button>
          )}

          <button
            className={`scroll-to-top ${showScrollTop ? 'visible' : ''} ${session ? 'with-fab' : ''}`}
            onClick={scrollToTop}
            aria-label="Scroll to top"
          >
            <ArrowUp size={20} />
          </button>

          {error && (
            <div className="error-message">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

		  <FeedTabs 
			activeTab={activeTab}
			onTabChange={handleTabChange}
			country={country}
		  />

          <div className="posts-container">
            {isChangingFeed ? (
              <div className="feed-loading">
                <Loader2 size={20} className="loading-spinner" />
                Loading feed...
              </div>
            ) : activeTab === 'saved' ? (
              <SavedPosts
                bookmarkService={bookmarkService}
                bluesky={bluesky}
                isLoggedIn={!!session}
                onLoginRequired={() => setShowLoginModal(true)}
                onLike={handleLike}
                onRepost={handleRepost}
              />
			) : !Array.isArray(posts) || posts.length === 0 ? (
			  <div className="no-posts-message">
				{isRefreshing ? t('feed.loading') : t('feed.noPostsYet')}
			  </div>
			) : (
			  <>
	            {posts.map((post) => (
              <Post
                key={post.post?.cid || post.cid}
                post={post}
                onLike={handleLike}
                onRepost={handleRepost}
                onLoginRequired={() => setShowLoginModal(true)}
                isLoggedIn={!!session}
                isLoading={isLoading}
                bluesky={bluesky}
                bookmarkService={bookmarkService}
              />
            ))}

	                {/* Load more trigger */}
                <div ref={loadMoreRef} className="load-more-trigger">
                  {isLoadingMore && (
                    <div className="loading-more">
                      <Loader2 size={18} className="loading-spinner" />
                      Loading more...
                    </div>
                  )}
                </div>

				{!hasMore && posts.length > 0 && (
				  <div className="no-more-posts">
					No more posts to load
				  </div>
				)}
			  </>
			)}
		  </div>

		  <Modal 
			isOpen={showLoginModal} 
			onClose={() => {
			  setShowLoginModal(false);
			  setError('');
			}}
		  >
			<LoginForm
			  onSubmit={handleLogin}
			  isLoading={isLoading}
			/>
			{error && <div className="error-message">{error}</div>}
		  </Modal>

		  <Modal
			isOpen={showCreatePost}
			onClose={() => {
			  setShowCreatePost(false);
			  setError('');
			}}
		  >
			<CreatePost
			  onSubmit={handleCreatePost}
			  isLoading={isLoading}
			  country={country}
			/>
			{error && <div className="error-message">{error}</div>}
		  </Modal>
		</>
	  )}

      {/* Fade gradient at bottom of feed */}
      <div className="feed-fade" aria-hidden="true" />
	</div>
  );
};

export default GovSky;